import { TourBagIronDetail, TourBagIronSet } from '@/api/models';
import { IActivePlayer } from '@/db/repositories/ActivePlayerRepo';
import { ILieOptionMap, ILoftOptionMap } from '@/library/LoftLieHelper';
import { ShaftLengthOptionMap } from '@/library/ShaftLengthHelper';
import { ISwingWtOptionMap } from '@/library/SwingWeightHelper';
import { IShaftOptionMap } from '@/library/ShaftHelper';
import { IIronDynamicDropdownData, IIronStaticDropdownData } from './useEditIron';
import { IIronClub } from './IronList';
import { isIOSWebView } from '@/utils';
import { handFullNameToAbbreviation } from '@/library/HandHelper';

interface IBuildIronPayloadProps {
  ironClub: IIronClub;
  loftDropdownData: ILoftOptionMap | undefined;
  lieDropdownData: ILieOptionMap | undefined;
  ironStaticDropdownData: IIronStaticDropdownData | undefined;
  ironDynamicDropdownData: IIronDynamicDropdownData | undefined;
  shaftDropdownData: IShaftOptionMap | undefined;
  shaftLengthDropdownData: ShaftLengthOptionMap | undefined;
  swingWtDropdownData: ISwingWtOptionMap | undefined;
}

export const buildIronPayload = ({
  ironClub,
  ironStaticDropdownData,
  ironDynamicDropdownData,
  lieDropdownData,
  loftDropdownData,
  shaftDropdownData,
  shaftLengthDropdownData,
  swingWtDropdownData,
}: IBuildIronPayloadProps): Partial<TourBagIronDetail> => {
  // fill non-id raw text values - ie familyName - since its part of the expected payload
  const lieData = lieDropdownData?.map[ironClub.lie];
  const loftData = loftDropdownData?.map[ironClub.loft];
  const swingWtData = swingWtDropdownData?.map[ironClub.swingWt];
  const shaftLengthData = shaftLengthDropdownData?.map[ironClub.ironShaftLength];

  return {
    // iron props
    isActive: ironClub.isActive,
    hand: handFullNameToAbbreviation(ironClub.ironHand),
    familyId: ironClub.ironFamilyId,
    familyName: (ironClub.ironFamilyId && ironStaticDropdownData?.ironFamily.map[ironClub.ironFamilyId].name) || '',
    headModelId: ironClub.ironHeadModelId,
    headModelName: (ironClub.ironHeadModelId && ironDynamicDropdownData?.ironHeadModel.map[ironClub.ironHeadModelId].name) || '',
    ironClubId: ironClub.ironClubTypeId,
    ironClubName: (ironClub.ironClubTypeId && ironStaticDropdownData?.ironClub.map[ironClub.ironClubTypeId].name) || '',
    // ironShaftId: ironClub.ironShaftId, // TODO: Request API to return shaft id as well
    shaftName: (ironClub.ironShaftId && shaftDropdownData?.map[ironClub.ironShaftId].name) || '',
    isShaftLengthFinished: ironClub.measured,
    shaftCosmetic: ironClub.ironShaftCosmetic,
    gripType: ironClub.ironGripType,
    gripSizeId: ironClub.ironGripSizeId,
    gripSize: (ironClub.ironGripSizeId && ironStaticDropdownData?.gripSize.map[ironClub.ironGripSizeId].name) || '',
    tape: ironClub.ironGripTape,
    rndRib: ironClub.ironRoundRib,
    logo: ironClub.ironLogo,
    notes: ironClub.notes,
    // loft & lie
    lieDigit: lieData?.lieDigit || '',
    lieDigitId: lieData?.joinTableDigitId,
    lieDigitId1: lieData?.sourceTableDigitId,
    lieFraction: lieData?.lieFraction || '',
    lieFractionId: lieData?.joinTableFractionId,
    lieFractionId1: lieData?.sourceTableFractionId,
    loftDigit: loftData?.loftDigit || '',
    loftDigitId: loftData?.joinTableDigitId,
    loftDigitId1: loftData?.sourceTableDigitId,
    loftFraction: loftData?.loftFraction || '',
    loftFractionId: loftData?.joinTableFractionId,
    loftFractionId1: loftData?.sourceTableFractionId,
    // swing wt
    swingWtPrefixId: swingWtData?.swingWtPrefixId,
    swingWtPrefixName: swingWtData?.swingWtPrefixName || '',
    swingWtSuffixId: swingWtData?.swingWtSuffixId,
    swingWtSuffixName: swingWtData?.swingWtSuffixName || '',
    // shaft length
    shaftLengthDigit: shaftLengthData?.digit || '',
    shaftLengthDigitId: shaftLengthData?.joinTableDigitId,
    shaftLengthDigitId1: Number(shaftLengthData?.sourceTableDigitId),
    shaftLengthFraction: shaftLengthData?.fraction || '',
    shaftLengthFractionId: Number(shaftLengthData?.joinTableFractionId),
    shaftLengthFractionId1: Number(shaftLengthData?.sourceTableFractionId),
    // final hw
    headWeight: ironClub.headWeight,
    drill: ironClub.drill,
    tipPlug: ironClub.tipPlug,
    glueWeight: ironClub.glueWeight,
    leadTape: ironClub.leadTape,
    headWeightLocationBackLookupId: ironClub.headWeightLocationBackLookupId,
    headWeightLocationBackLookupName: ironClub.headWeightLocationBackLookupName,
    headWeightNumberBackLookupId: ironClub.headWeightNumberBackLookupId,
    headWeightNumberBackLookupName: ironClub.headWeightNumberBackLookupName,
  };
};

interface IBuildSetIronPayloadProps {
  iron: TourBagIronDetail;
  set: TourBagIronSet;
  action: 'create' | 'edit';
  tourBagMstrId: number | undefined;
  loftDropdownData: ILoftOptionMap | undefined;
  lieDropdownData: ILieOptionMap | undefined;
  ironStaticDropdownData: IIronStaticDropdownData | undefined;
  ironDynamicDropdownData: IIronDynamicDropdownData | undefined;
  shaftLengthDropdownData: ShaftLengthOptionMap | undefined;
  swingWtDropdownData: ISwingWtOptionMap | undefined;
}

export const buildSetIronPayload = ({
  iron,
  set,
  action,
  tourBagMstrId,
  ironStaticDropdownData,
  ironDynamicDropdownData,
  lieDropdownData,
  loftDropdownData,
  shaftLengthDropdownData,
  swingWtDropdownData,
}: IBuildSetIronPayloadProps): Partial<TourBagIronDetail> => {
  const isCreateMode = action === 'create'; // For creation we set set and iron primary keys to 0
  // fill non-id raw text values - ie familyName - since its part of the expected payload
  const lieData = lieDropdownData?.map[`${iron.lieDigit}${iron.lieFraction}`];
  const loftData = loftDropdownData?.map[`${iron.loftDigit}${iron.loftFraction}`];
  const shaftLengthData = shaftLengthDropdownData?.map[`${iron.shaftLengthDigit} ${iron.shaftLengthFraction}`];
  const swingWtData = swingWtDropdownData?.map[`${set.swingWtPrefixName}${set.swingWtSuffixName}`];

  return {
    // spread iron props
    ...iron,
    // iron props
    familyId: iron.familyId,
    familyName: (iron.familyId && ironStaticDropdownData?.ironFamily.map[iron.familyId].name) || '',
    headModelId: iron.headModelId,
    headModelName: (iron.headModelId && ironDynamicDropdownData?.ironHeadModel.map[iron.headModelId].name) || '',
    ironClubId: iron.ironClubId,
    ironClubName: (iron.ironClubId && ironStaticDropdownData?.ironClub.map[iron.ironClubId].name) || '',

    // set properties
    tourBagMstrId: tourBagMstrId || 0,
    tourBagIronSetId: isCreateMode ? 0 : set.tourBagIronSetId || 0, // TODO: API Requires 0 for tourBagIronSetId, update backend to accomodate -1 as the rest
    tourBagIronDetailId: isCreateMode ? 0 : iron.tourBagIronDetailId || 0,
    hand: set.hand, // set hand already resolves to the right label ie : 'Right' | 'Left'
    shaftName: iron.shaftName,
    // shaftId: set.shaftId, // TODO: Request API to return shaft id as well
    isShaftLengthFinished: set.isShaftLengthFinished,
    shaftCosmetic: set.shaftCosmetic,
    // swing wt
    swingWtPrefixId: swingWtData?.swingWtPrefixId,
    swingWtPrefixName: swingWtData?.swingWtPrefixName || '',
    swingWtSuffixId: swingWtData?.swingWtSuffixId,
    swingWtSuffixName: swingWtData?.swingWtSuffixName || '',
    gripType: set.gripType,
    gripSize: set.gripSize,
    gripSizeId: set.gripSizeId,
    rndRib: set.rndRib,
    logo: set.logo,
    tape: set.tape,

    // loft & lie
    lieDigit: lieData?.lieDigit || '',
    lieDigitId: lieData?.joinTableDigitId,
    lieDigitId1: lieData?.sourceTableDigitId,
    lieFraction: lieData?.lieFraction || '',
    lieFractionId: lieData?.joinTableFractionId,
    lieFractionId1: lieData?.sourceTableFractionId,
    loftDigit: loftData?.loftDigit || '',
    loftDigitId: loftData?.joinTableDigitId,
    loftDigitId1: loftData?.sourceTableDigitId,
    loftFraction: loftData?.loftFraction || '',
    loftFractionId: loftData?.joinTableFractionId,
    loftFractionId1: loftData?.sourceTableFractionId,
    // shaft length
    shaftLengthDigit: shaftLengthData?.digit || '',
    shaftLengthDigitId: shaftLengthData?.joinTableDigitId,
    shaftLengthDigitId1: shaftLengthData && Number(shaftLengthData?.sourceTableDigitId),
    shaftLengthFraction: shaftLengthData?.fraction || '',
    shaftLengthFractionId: shaftLengthData && Number(shaftLengthData?.joinTableFractionId),
    shaftLengthFractionId1: shaftLengthData && Number(shaftLengthData?.sourceTableFractionId),
  };
};

export const buildSetSharedProps = (set: TourBagIronSet, iron: TourBagIronDetail) => ({
  // set properties
  hand: set.hand,
  shaftName: iron.shaftName || set.shaftName,
  // shaftId: set.shaftId, // TODO: Request API to return shaft id as well
  isShaftLengthFinished: Boolean(set.isShaftLengthFinished),
  shaftCosmetic: set.shaftCosmetic,
  // swing wt
  swingWtPrefixId: set.swingWtPrefixId,
  swingWtPrefixName: set.swingWtPrefixName || '',
  swingWtSuffixId: set.swingWtSuffixId,
  swingWtSuffixName: set.swingWtSuffixName || '',
  gripType: set.gripType,
  gripSize: set.gripSize,
  gripSizeId: set.gripSizeId,
  rndRib: set.rndRib,
  logo: set.logo,
  tape: set.tape,
});

interface IBuildIronMetadataProps {
  player: IActivePlayer | null;
}

export const buildIronMetadata = ({ player }: IBuildIronMetadataProps) => ({
  // player props
  playerId: Number(player?.playerId),
  playerName: player?.fullName || '',
  // tour props
  tourId: Number(player?.defaultTourId),
  tourName: player?.defaultTourName || '',
  // edit props
  editSource: isIOSWebView ? 'iOS' : 'web',
});
